
import { Component, Vue, Watch } from 'vue-property-decorator';
import {
  AllergenAdminDashboardResponse,
  AllergenAdminJobSummary,
  AllergenAdminMetricsSummary,
  AllergenAdminReviewQueueItem,
  MlModelStatusResponse,
  TestAllergenMlModelRequest,
  TestAllergenMlModelRow,
  TestAllergenMlModelResponse
} from '@/models/AllergenML/AllergenML';
import { NutrientSource } from '@/models/NutrientSource/NutrientSource';
import AllergenMLRepository from '@/utils/API/AllergenMLRepository';
import IngredientsRepository from '@/utils/API/IngredientsRepository';
import vModal from '@/components/vModal.vue';

@Component({
  components: {
    vModal,
  },
})
class AdminAllergensAi extends Vue {
  readonly adminDefaultJurisdiction = NutrientSource.Canada;
  readonly adminIngredientSearchNutrientSources = [
    NutrientSource.Canada,
    NutrientSource.US,
    NutrientSource.UK,
    NutrientSource.Branded,
    NutrientSource.Foundation,
    NutrientSource.SugarAlcohols,
    NutrientSource.Fndds,
  ];
  dashboardMetrics: AllergenAdminMetricsSummary = {
    ingredientResultsCount: 0,
    pendingReviewCount: 0,
    recipeAggregateCount: 0,
    unknownAliasCount: 0,
  };
  jobs: AllergenAdminJobSummary[] = [];
  reviewRows: AllergenAdminReviewQueueItem[] = [];
  selectedJobId: number | null = null;
  startIngredientId: number | null = null;
  selectedStartIngredientLabel = '';
  processFullCatalog = true;
  ingredientLimit: number | null = null;
  batchSize = 1000;
  showReviewQueueModal = false;
  reviewSearch = '';
  reviewPage = 1;
  reviewPageSize = 20;
  loadingDashboard = false;
  loadingModelStatus = false;
  loadingReviewQueue = false;
  runningAllergenJob = false;
  trainingModel = false;
  testingModel = false;
  adminActionsExpanded = true;
  operationsExpanded = true;
  modelTrainingExpanded = true;
  jobActionInFlight = false;
  exportingModelTest = false;
  dashboardPollingHandle: number | null = null;
  modelStatus: MlModelStatusResponse | null = null;
  modelTestResult: TestAllergenMlModelResponse | null = null;
  trainingTemplateFile: File | null = null;
  testSourceType = 'SystemRecipeData';
  testIngredientId: number | null = null;
  selectedTestIngredientLabel = '';
  testBatchStartIngredientId: number | null = null;
  selectedTestBatchStartIngredientLabel = '';
  testBatchIngredientLimit: number | null = 25;
  systemTrainingExampleCount = 0;
  uploadedTrainingExampleCount = 0;
  overrideSelections: Record<number, string[]> = {};
  selectedReviewRowIds: number[] = [];
  openAllergenDropdownRowId: number | null = null;
  allergenOptions = ['Milk', 'Eggs', 'Fish', 'Shellfish', 'Tree Nuts', 'Peanuts', 'Wheat', 'Soy', 'Sesame', 'Mustard', 'Sulphites'];

  get metricCards() {
    return [
      {
        id: 'analyzed',
        value: this.formatCompactCount(this.dashboardMetrics.ingredientResultsCount),
        hint: 'Saved ingredient-level allergen analyses'
      },
      {
        id: 'review',
        value: this.formatCompactCount(this.dashboardMetrics.pendingReviewCount),
        hint: 'Queued for human validation'
      },
      {
        id: 'recipes',
        value: this.formatCompactCount(this.dashboardMetrics.recipeAggregateCount),
        hint: 'Saved recipe and combined recipe aggregates'
      },
      {
        id: 'unknown',
        value: this.formatCompactCount(this.dashboardMetrics.unknownAliasCount),
        hint: 'Unknown alias candidates in the library'
      },
    ];
  }

  get selectedJob() {
    return this.allergenJobs.find((job) => job.id === this.selectedJobId) || this.allergenJobs[0] || null;
  }

  get allergenJobs() {
    return this.jobs.filter((job) => job.jobType === 'SetIngredientAllergens');
  }

  get totalToBeReviewed() {
    return this.dashboardMetrics.pendingReviewCount;
  }

  get totalReviewPages() {
    return Math.max(Math.ceil(this.totalToBeReviewed / this.reviewPageSize), 1);
  }

  get paginatedReviewRows() {
    return this.reviewRows;
  }

  get hasSelectedReviewRows() {
    return this.selectedReviewRowIds.length > 0;
  }

  get areAllVisibleReviewRowsSelected() {
    return this.paginatedReviewRows.length > 0
      && this.paginatedReviewRows.every((row) => this.selectedReviewRowIds.includes(row.id));
  }

  get detailPanelTitle() {
    return 'Job detail';
  }

  get modelStatusHeadline() {
    return this.modelStatus?.hasTrainedModel ? 'Learned model active' : 'No trained model loaded';
  }

  get systemTrainButtonLabel() {
    return this.modelStatus?.defaultTrainButtonLabel || 'Train Model (System Data)';
  }

  get exportTrainButtonLabel() {
    return this.modelStatus?.exportTrainButtonLabel || 'Train Model (Export)';
  }

  get modelDisplayName() {
    const name = this.modelStatus?.activeModelName;
    const version = this.modelStatus?.activeModelVersion;

    if (name && version) {
      return `${name} (${version})`;
    }

    return name || version || 'Not available';
  }

  get modelTrainingProfile() {
    return this.modelStatus?.activeTrainingProfile || 'Not available';
  }

  get trainingTemplateFileName() {
    return this.trainingTemplateFile?.name || '';
  }

  get isDatabaseTestMode() {
    return this.testSourceType === 'DatabaseSingleIngredient' || this.testSourceType === 'DatabaseIngredientSelection';
  }

  get isExportTemplateTestMode() {
    return this.testSourceType === 'ExportTemplate';
  }

  get isSingleIngredientTestMode() {
    return this.testSourceType === 'DatabaseSingleIngredient';
  }

  get isBatchIngredientTestMode() {
    return this.testSourceType === 'DatabaseIngredientSelection';
  }

  get modelTestCards() {
    if (!this.modelTestResult) {
      return [];
    }

    return [
      {
        label: 'Accuracy',
        value: this.formatPercent(this.modelTestResult.exactMatchAccuracy),
        hint: `${this.modelTestResult.exactMatchCount.toLocaleString()} exact matches`,
      },
      {
        label: 'Precision',
        value: this.formatPercent(this.modelTestResult.precision),
        hint: 'Positive prediction quality',
      },
      {
        label: 'Recall',
        value: this.formatPercent(this.modelTestResult.recall),
        hint: 'Captured expected allergens',
      },
      {
        label: 'F1 Score',
        value: this.formatPercent(this.modelTestResult.f1Score),
        hint: 'Balanced precision and recall',
      },
      {
        label: 'Avg Confidence',
        value: this.formatPercent(this.modelTestResult.averagePredictionConfidence),
        hint: 'Average model confidence',
      },
      {
        label: 'Test Rows',
        value: this.modelTestResult.testExampleCount.toLocaleString(),
        hint: `${(this.modelTestResult.systemTestExampleCount || 0).toLocaleString()} system | ${this.modelTestResult.uploadedTestExampleCount.toLocaleString()} uploaded`,
      },
    ];
  }

  get modelTestSummaryCaption() {
    if (!this.modelTestResult) {
      return '';
    }

    return `Showing the latest allergen AI test model accuracy data for ${this.modelTestResult.modelName} (${this.modelTestResult.modelVersion}) using ${this.modelTestResult.testScopeLabel || 'the current test scope'}.`;
  }

  toggleSection(section: 'adminActions' | 'operations' | 'modelTraining') {
    if (section === 'adminActions') {
      this.adminActionsExpanded = !this.adminActionsExpanded;
      return;
    }

    if (section === 'operations') {
      this.operationsExpanded = !this.operationsExpanded;
      return;
    }

    this.modelTrainingExpanded = !this.modelTrainingExpanded;
  }

  mounted() {
    this.fetchAdminDashboard();
    this.fetchModelStatus();
    this.startDashboardPolling();
    this.initStartIngredientSelect();
    document.addEventListener('click', this.handleDocumentClick);
  }

  beforeDestroy() {
    this.stopDashboardPolling();
    this.destroyStartIngredientSelect();
    this.destroyTestIngredientSelect();
    this.destroyTestBatchStartIngredientSelect();
    document.removeEventListener('click', this.handleDocumentClick);
  }

  fetchAdminDashboard() {
    this.loadingDashboard = true;

    AllergenMLRepository.getAdminDashboard()
      .then((response) => {
        const dashboard: AllergenAdminDashboardResponse = response.data;
        this.dashboardMetrics = dashboard.metrics || this.dashboardMetrics;
        this.jobs = dashboard.jobs || [];
        if (!this.selectedJobId || !this.jobs.find((job) => job.id === this.selectedJobId)) {
          this.selectedJobId = this.allergenJobs[0]?.id || null;
        }
        if (!this.showReviewQueueModal) {
          this.reviewRows = dashboard.reviewQueuePreview || [];
        }
        if (this.showReviewQueueModal) {
          this.fetchReviewQueue(this.reviewPage);
        }
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to load the AI Allergen Detector admin dashboard.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.loadingDashboard = false;
      });
  }

  fetchModelStatus(showSuccessMessage = false) {
    this.loadingModelStatus = true;

    AllergenMLRepository.getModelStatus()
      .then((response) => {
        this.modelStatus = response.data;
        this.modelTestResult = response.data.latestTestResult || null;
        this.systemTrainingExampleCount = response.data.trainingExampleCount || 0;
        if (showSuccessMessage && (this as any).$snotify) {
          (this as any).$snotify.success('Model status refreshed.');
        }
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to load allergen model status.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.loadingModelStatus = false;
      });
  }

  trainSystemModel() {
    this.trainingModel = true;
    const trainRequest = AllergenMLRepository.trainModel({
      activateModel: true,
      useSystemTrainingData: true,
      nutrientSource: this.adminDefaultJurisdiction,
    });

    trainRequest
      .then((response) => {
        this.systemTrainingExampleCount = response.data.systemTrainingExampleCount || 0;
        this.uploadedTrainingExampleCount = response.data.uploadedTrainingExampleCount || 0;
        this.modelStatus = {
          hasTrainedModel: true,
          activeModelName: response.data.modelName,
          activeModelVersion: response.data.modelVersion,
          activeTrainingProfile: this.modelStatus?.activeTrainingProfile || 'Learned',
          trainingExampleCount: response.data.trainingExampleCount,
          vocabularySize: response.data.vocabularySize,
          allergenClassCount: response.data.allergenClassCount,
          trainedOnUtc: response.data.trainedOnUtc,
          modelPath: response.data.modelPath,
          pendingSystemTrainingExampleCount: 0,
          pendingRecipeFlaggedIngredientAllergenCount: 0,
          defaultTrainButtonLabel: this.systemTrainButtonLabel,
          exportTrainButtonLabel: this.exportTrainButtonLabel,
          latestTestResult: this.modelTestResult,
        };
        if ((this as any).$snotify) {
          (this as any).$snotify.success(
            `Allergen model trained with ${response.data.systemTrainingExampleCount || 0} system rows from ${response.data.recipeFlaggedIngredientAllergenCount || 0} recipe flagged ingredient allergens.`
          );
        }
        this.fetchModelStatus();
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to train the allergen model.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.trainingModel = false;
      });
  }

  trainExportModel() {
    if (!this.trainingTemplateFile) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Upload an export template before running export training.');
      }
      return;
    }

    this.trainingModel = true;

    this.trainModelWithTemplateRequest()
      .then((response) => {
        this.systemTrainingExampleCount = response.data.systemTrainingExampleCount || 0;
        this.uploadedTrainingExampleCount = response.data.uploadedTrainingExampleCount || 0;
        this.modelStatus = {
          hasTrainedModel: true,
          activeModelName: response.data.modelName,
          activeModelVersion: response.data.modelVersion,
          activeTrainingProfile: this.modelStatus?.activeTrainingProfile || 'Learned',
          trainingExampleCount: response.data.trainingExampleCount,
          vocabularySize: response.data.vocabularySize,
          allergenClassCount: response.data.allergenClassCount,
          trainedOnUtc: response.data.trainedOnUtc,
          modelPath: response.data.modelPath,
          pendingSystemTrainingExampleCount: this.modelStatus?.pendingSystemTrainingExampleCount || 0,
          pendingRecipeFlaggedIngredientAllergenCount: this.modelStatus?.pendingRecipeFlaggedIngredientAllergenCount || 0,
          defaultTrainButtonLabel: this.systemTrainButtonLabel,
          exportTrainButtonLabel: this.exportTrainButtonLabel,
          latestTestResult: this.modelTestResult,
        };
        if ((this as any).$snotify) {
          (this as any).$snotify.success(
            `Allergen model trained with ${response.data.uploadedTrainingExampleCount || 0} export rows.`
          );
        }
        this.clearTrainingTemplate();
        this.fetchModelStatus();
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to train the allergen model from export data.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.trainingModel = false;
      });
  }

  trainModelWithTemplateRequest() {
    const formData = new FormData();
    formData.append('file', this.trainingTemplateFile as File);
    formData.append('activateModel', 'true');
    formData.append('nutrientSource', String(this.adminDefaultJurisdiction));

    return AllergenMLRepository.trainModelWithTemplate(formData);
  }

  testModel() {
    if (!this.validateModelTestSelection()) {
      return;
    }

    this.testingModel = true;

    const testRequest = this.isExportTemplateTestMode && this.trainingTemplateFile
      ? this.testModelWithTemplateRequest()
      : AllergenMLRepository.testModel(this.buildTestModelRequest());

    testRequest
      .then((response) => {
        this.modelTestResult = response.data;
        if (this.modelStatus) {
          this.modelStatus = {
            ...this.modelStatus,
            latestTestResult: response.data,
          };
        }
        if ((this as any).$snotify) {
          (this as any).$snotify.success(
            `Model test completed. Exact match ${this.formatPercent(response.data.exactMatchAccuracy)}, precision ${this.formatPercent(response.data.precision)}, recall ${this.formatPercent(response.data.recall)}.`
          );
        }
        this.clearTrainingTemplate();
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to test the allergen model.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.testingModel = false;
      });
  }

  exportTestModelReport() {
    if (!this.validateModelTestSelection()) {
      return;
    }

    this.exportingModelTest = true;

    const exportRequest = this.isExportTemplateTestMode && this.trainingTemplateFile
      ? this.exportTestModelReportWithTemplateRequest()
      : AllergenMLRepository.exportTestModelReport(this.buildTestModelRequest());

    exportRequest
      .then((response) => {
        this.downloadWorkbookBlob(response.data, 'allergen-ml-test-report.xlsx');
        if ((this as any).$snotify) {
          (this as any).$snotify.success('Allergen ML test report exported.');
        }
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to export the allergen ML test report.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.exportingModelTest = false;
      });
  }

  testModelWithTemplateRequest() {
    const formData = new FormData();
    formData.append('file', this.trainingTemplateFile as File);
    formData.append('nutrientSource', String(this.adminDefaultJurisdiction));

    return AllergenMLRepository.testModelWithTemplate(formData);
  }

  exportTestModelReportWithTemplateRequest() {
    const formData = new FormData();
    formData.append('file', this.trainingTemplateFile as File);
    formData.append('nutrientSource', String(this.adminDefaultJurisdiction));

    return AllergenMLRepository.exportTestModelReportWithTemplate(formData);
  }

  downloadTrainingTemplate() {
    AllergenMLRepository.downloadTrainingTemplate()
      .then((response) => {
        this.downloadWorkbookBlob(response.data, 'allergen-training-template.xlsx');
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to download the allergen training template.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      });
  }

  handleTrainingTemplateSelected(event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.trainingTemplateFile = target?.files?.[0] || null;
  }

  clearTrainingTemplate() {
    this.trainingTemplateFile = null;
    const trainingInput = this.$el?.querySelector?.('#allergenTrainingTemplateInput') as HTMLInputElement | null;
    if (trainingInput) {
      trainingInput.value = '';
    }
  }

  fetchReviewQueue(page = this.reviewPage) {
    this.loadingReviewQueue = true;

    AllergenMLRepository.getReviewQueue(this.reviewSearch, page, this.reviewPageSize)
      .then((response) => {
        this.reviewPage = response.data.page;
        this.reviewRows = response.data.items || [];
        this.selectedReviewRowIds = this.selectedReviewRowIds.filter((id) => this.reviewRows.some((row) => row.id === id));
        this.overrideSelections = Object.keys(this.overrideSelections)
          .filter((key) => this.reviewRows.some((row) => row.id === Number(key)))
          .reduce((accumulator, key) => {
            accumulator[Number(key)] = this.overrideSelections[Number(key)];
            return accumulator;
          }, {} as Record<number, string[]>);
      })
      .catch(() => {
        if ((this as any).$snotify) {
          (this as any).$snotify.error('Unable to load the review queue.');
        }
      })
      .finally(() => {
        this.loadingReviewQueue = false;
      });
  }

  @Watch('processFullCatalog', { immediate: true })
  onProcessFullCatalogChanged(isFullCatalog: boolean) {
    if (isFullCatalog) {
      this.startIngredientId = null;
      this.selectedStartIngredientLabel = '';
      this.ingredientLimit = null;
    }

    this.syncStartIngredientSelectState();
  }

  @Watch('testSourceType')
  onTestSourceTypeChanged() {
    this.destroyTestIngredientSelect();
    this.destroyTestBatchStartIngredientSelect();

    if (this.isDatabaseTestMode) {
      this.clearTrainingTemplate();
    }

    if (!this.isSingleIngredientTestMode) {
      this.testIngredientId = null;
      this.selectedTestIngredientLabel = '';
    }

    if (!this.isBatchIngredientTestMode) {
      this.testBatchStartIngredientId = null;
      this.selectedTestBatchStartIngredientLabel = '';
    }

    this.$nextTick(() => {
      if (this.isSingleIngredientTestMode) {
        this.initTestIngredientSelect();
      }

      if (this.isBatchIngredientTestMode) {
        this.initTestBatchStartIngredientSelect();
      }
    });
  }

  canPauseJob(job: AllergenAdminJobSummary | null) {
    if (!job) {
      return false;
    }

    const normalizedStatus = (job.status || '').toLowerCase();
    return normalizedStatus === 'queued' || normalizedStatus === 'running';
  }

  canCancelJob(job: AllergenAdminJobSummary | null) {
    if (!job) {
      return false;
    }

    const normalizedStatus = (job.status || '').toLowerCase();
    return normalizedStatus === 'queued' || normalizedStatus === 'running' || normalizedStatus === 'paused';
  }

  canResumeJob(job: AllergenAdminJobSummary | null) {
    if (!job) {
      return false;
    }

    const normalizedStatus = (job.status || '').toLowerCase();
    const resumableStatus = normalizedStatus === 'paused'
      || normalizedStatus === 'cancelled'
      || normalizedStatus === 'failed'
      || normalizedStatus === 'partial';

    return resumableStatus && job.processedCount < job.totalCount;
  }

  pauseSelectedJob() {
    if (!this.selectedJob || !this.canPauseJob(this.selectedJob)) {
      return;
    }

    this.jobActionInFlight = true;

    AllergenMLRepository.pauseJob(this.selectedJob.id)
      .then((response) => {
        this.selectedJobId = response.data.id;
        if ((this as any).$snotify) {
          (this as any).$snotify.success('Allergen job paused.');
        }
        this.fetchAdminDashboard();
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to pause the allergen job.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.jobActionInFlight = false;
      });
  }

  cancelSelectedJob() {
    if (!this.selectedJob || !this.canCancelJob(this.selectedJob)) {
      return;
    }

    this.jobActionInFlight = true;

    AllergenMLRepository.cancelJob(this.selectedJob.id)
      .then((response) => {
        this.selectedJobId = response.data.id;
        if ((this as any).$snotify) {
          (this as any).$snotify.success('Allergen job cancelled.');
        }
        this.fetchAdminDashboard();
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to cancel the allergen job.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.jobActionInFlight = false;
      });
  }

  resumeSelectedJob() {
    if (!this.selectedJob || !this.canResumeJob(this.selectedJob)) {
      return;
    }

    this.jobActionInFlight = true;

    AllergenMLRepository.resumeJob(this.selectedJob.id)
      .then((response) => {
        this.selectedJobId = response.data.id;
        if ((this as any).$snotify) {
          (this as any).$snotify.success('Allergen job resumed.');
        }
        this.fetchAdminDashboard();
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to resume the allergen job.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.jobActionInFlight = false;
      });
  }

  runAllergenJob() {
    if (!this.processFullCatalog && (!this.startIngredientId || this.startIngredientId <= 0)) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Select a start ingredient before running the allergen job.');
      }
      return;
    }

    if (!this.processFullCatalog && (!this.ingredientLimit || this.ingredientLimit <= 0)) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Enter a positive ingredient count or choose full matching catalog.');
      }
      return;
    }

    if (!this.batchSize || this.batchSize <= 0) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Enter a positive batch size.');
      }
      return;
    }

    this.runningAllergenJob = true;

    AllergenMLRepository.runSetIngredientAllergens({
      scopeType: 'Full Catalog',
      changedSinceUtc: null,
      startIngredientId: this.processFullCatalog ? null : this.startIngredientId,
      ingredientLimit: this.processFullCatalog ? null : this.ingredientLimit,
      nutrientSource: this.adminDefaultJurisdiction,
      batchSize: this.batchSize,
    })
      .then((response) => {
        this.selectedJobId = response.data.id;
        if ((this as any).$snotify) {
          (this as any).$snotify.success('Set Ingredient Allergens job started.');
        }
        this.fetchAdminDashboard();
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
          || error?.response?.data?.message
          || 'Unable to run Set Ingredient Allergens.';
        if ((this as any).$snotify) {
          (this as any).$snotify.error(message);
        }
      })
      .finally(() => {
        this.runningAllergenJob = false;
      });
  }

  buildTestModelRequest(): TestAllergenMlModelRequest {
    const request: TestAllergenMlModelRequest = {
      nutrientSource: this.adminDefaultJurisdiction,
      testSourceType: 'SystemRecipeData',
      useSystemTestData: true,
    };

    if (this.isSingleIngredientTestMode) {
      request.testSourceType = 'DatabaseSingleIngredient';
      request.ingredientId = this.testIngredientId;
      request.useSystemTestData = false;
      return request;
    }

    if (this.isBatchIngredientTestMode) {
      request.testSourceType = 'DatabaseIngredientSelection';
      request.startIngredientId = this.testBatchStartIngredientId;
      request.ingredientLimit = this.testBatchIngredientLimit;
      request.useSystemTestData = false;
      return request;
    }

    if (this.isExportTemplateTestMode) {
      request.testSourceType = 'ExportTemplate';
      request.useSystemTestData = false;
    }

    return request;
  }

  validateModelTestSelection() {
    if (this.isDatabaseTestMode && this.trainingTemplateFile) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Clear the uploaded template before running a database ingredient ML test.');
      }
      return false;
    }

    if (this.isExportTemplateTestMode && !this.trainingTemplateFile) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Upload an export template before running export testing.');
      }
      return false;
    }

    if (this.isSingleIngredientTestMode && (!this.testIngredientId || this.testIngredientId <= 0)) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Select a database ingredient before running a single ingredient model test.');
      }
      return false;
    }

    if (this.isBatchIngredientTestMode && (!this.testBatchStartIngredientId || this.testBatchStartIngredientId <= 0)) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Select a batch start ingredient before running a database batch model test.');
      }
      return false;
    }

    if (this.isBatchIngredientTestMode && (!this.testBatchIngredientLimit || this.testBatchIngredientLimit <= 0)) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Enter a positive batch ingredient count before running a database batch model test.');
      }
      return false;
    }

    return true;
  }

  initStartIngredientSelect() {
    this.$nextTick(() => {
      const selector = (<any>$)('#allergenStartIngredientSelect');
      if (!selector.length) {
        return;
      }

      if (selector.hasClass('select2-hidden-accessible')) {
        selector.off();
        selector.select2('destroy');
      }

      selector.select2({
        allowClear: true,
        width: '100%',
        placeholder: 'Search ingredient',
        minimumInputLength: 2,
        ajax: {
          delay: 350,
          transport: (params: any, success: any, failure: any) => {
            IngredientsRepository.searchIngredients({
              nutrientSources: this.adminIngredientSearchNutrientSources,
              maxResultsCount: 50,
              includeCustomIngredients: false,
              searchTerm: params.data.term || '',
              recipeId: null,
            })
              .then(success)
              .catch(failure);
          },
          processResults: (response: any) => {
            const ingredients = response?.data?.result?.dbIngredients || [];
            return {
              results: ingredients.map((ingredient: any) => ({
                id: ingredient.id,
                text: `${ingredient.id} - ${ingredient.name} (${this.formatNutrientSourceName(ingredient.nutrientSource)})`,
              })),
            };
          },
        },
        escapeMarkup(element: string) {
          return element;
        },
      })
        .on('select2:select', (eventData: any) => {
          const selected = eventData.params.data;
          this.startIngredientId = Number(selected.id);
          this.selectedStartIngredientLabel = selected.text;
        })
        .on('select2:clear', () => {
          this.startIngredientId = null;
          this.selectedStartIngredientLabel = '';
        });

      this.syncStartIngredientSelectState();
    });
  }

  initTestIngredientSelect() {
    this.initIngredientSearchSelect('#allergenTestIngredientSelect', (selected: any) => {
      this.testIngredientId = Number(selected.id);
      this.selectedTestIngredientLabel = selected.text;
    }, () => {
      this.testIngredientId = null;
      this.selectedTestIngredientLabel = '';
    });
  }

  initTestBatchStartIngredientSelect() {
    this.initIngredientSearchSelect('#allergenTestBatchStartIngredientSelect', (selected: any) => {
      this.testBatchStartIngredientId = Number(selected.id);
      this.selectedTestBatchStartIngredientLabel = selected.text;
    }, () => {
      this.testBatchStartIngredientId = null;
      this.selectedTestBatchStartIngredientLabel = '';
    });
  }

  initIngredientSearchSelect(selectorId: string, onSelect: (selected: any) => void, onClear: () => void) {
    this.$nextTick(() => {
      const selector = (<any>$)(selectorId);
      if (!selector.length) {
        return;
      }

      if (selector.hasClass('select2-hidden-accessible')) {
        selector.off();
        selector.select2('destroy');
      }

      selector.select2({
        allowClear: true,
        width: '100%',
        placeholder: 'Search ingredient',
        minimumInputLength: 2,
        ajax: {
          delay: 350,
          transport: (params: any, success: any, failure: any) => {
            IngredientsRepository.searchIngredients({
              nutrientSources: this.adminIngredientSearchNutrientSources,
              maxResultsCount: 50,
              includeCustomIngredients: false,
              searchTerm: params.data.term || '',
              recipeId: null,
            })
              .then(success)
              .catch(failure);
          },
          processResults: (response: any) => {
            const ingredients = response?.data?.result?.dbIngredients || [];
            return {
              results: ingredients.map((ingredient: any) => ({
                id: ingredient.id,
                text: `${ingredient.id} - ${ingredient.name} (${this.formatNutrientSourceName(ingredient.nutrientSource)})`,
              })),
            };
          },
        },
        escapeMarkup(element: string) {
          return element;
        },
      })
        .on('select2:select', (eventData: any) => onSelect(eventData.params.data))
        .on('select2:clear', onClear);
    });
  }

  syncStartIngredientSelectState() {
    this.$nextTick(() => {
      const selector = (<any>$)('#allergenStartIngredientSelect');
      if (!selector.length) {
        return;
      }

      selector.prop('disabled', this.processFullCatalog);

      if (this.processFullCatalog) {
        selector.val(null).trigger('change');
      }
    });
  }

  destroyStartIngredientSelect() {
    const selector = (<any>$)('#allergenStartIngredientSelect');
    if (selector.length && selector.hasClass('select2-hidden-accessible')) {
      selector.off();
      selector.select2('destroy');
    }
  }

  destroyTestIngredientSelect() {
    this.destroyIngredientSearchSelect('#allergenTestIngredientSelect');
  }

  destroyTestBatchStartIngredientSelect() {
    this.destroyIngredientSearchSelect('#allergenTestBatchStartIngredientSelect');
  }

  destroyIngredientSearchSelect(selectorId: string) {
    const selector = (<any>$)(selectorId);
    if (selector.length && selector.hasClass('select2-hidden-accessible')) {
      selector.off();
      selector.select2('destroy');
    }
  }

  clearSelect2Value(selectorId: string) {
    this.$nextTick(() => {
      const selector = (<any>$)(selectorId);
      if (selector.length) {
        selector.val(null).trigger('change');
      }
    });
  }

  formatNutrientSourceName(nutrientSource: NutrientSource | number | null | undefined) {
    switch (nutrientSource) {
      case NutrientSource.Canada:
        return 'Canada';
      case NutrientSource.US:
        return 'US';
      case NutrientSource.UK:
        return 'UK';
      case NutrientSource.Branded:
        return 'US Branded';
      case NutrientSource.Foundation:
        return 'US Foundation';
      case NutrientSource.SugarAlcohols:
        return 'Sugar Alcohols';
      case NutrientSource.Fndds:
        return 'FNDDS';
      default:
        return 'Unknown Source';
    }
  }

  formatPredictionEvidence(row: TestAllergenMlModelRow) {
    if (!row?.predictions?.length) {
      return 'No ML evidence';
    }

    return row.predictions
      .map((prediction) => `${prediction.id}: ${prediction.evidence?.join(', ') || 'no evidence'}`)
      .join(' | ');
  }

  downloadWorkbookBlob(data: BlobPart, fileName: string) {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }

  openReviewQueueModal() {
    this.showReviewQueueModal = true;
    this.selectedReviewRowIds = [];
    this.openAllergenDropdownRowId = null;
    this.fetchReviewQueue(1);
  }

  resolveReview(action: 'approved' | 'dismissed', id: number) {
    const selected = this.reviewRows.find((row) => row.id === id);
    if (!selected) {
      return;
    }

    const resolvedAllergenCodes = action === 'approved' ? this.getSelectedAllergenValues(selected) : [];
    if (action === 'approved' && !resolvedAllergenCodes.length) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Select at least one allergen.');
      }
      return;
    }

    AllergenMLRepository.resolveReview({
      reviewQueueItemId: id,
      action,
      resolvedAllergenCode: resolvedAllergenCodes[0] || null,
      resolvedAllergenCodes,
    })
      .then(() => {
        if ((this as any).$snotify) {
          (this as any).$snotify.success(action === 'approved' ? 'Approved.' : 'Rejected.');
        }
        this.fetchAdminDashboard();
        if (this.showReviewQueueModal) {
          const nextPage = Math.min(this.reviewPage, this.totalReviewPages);
          this.fetchReviewQueue(nextPage);
        }
      })
      .catch(() => {
        if ((this as any).$snotify) {
          (this as any).$snotify.error('Unable to update the selected item.');
        }
      });
  }

  resolveSelectedReviews(action: 'approved' | 'dismissed') {
    const selectedRows = this.reviewRows.filter((row) => this.selectedReviewRowIds.includes(row.id));
    if (!selectedRows.length) {
      return;
    }

    if (action === 'approved' && selectedRows.some((row) => !this.getSelectedAllergenValues(row).length)) {
      if ((this as any).$snotify) {
        (this as any).$snotify.warning('Select at least one allergen for each selected row.');
      }
      return;
    }

    this.loadingReviewQueue = true;

    Promise.all(selectedRows.map((row) => AllergenMLRepository.resolveReview({
      reviewQueueItemId: row.id,
      action,
      resolvedAllergenCode: action === 'approved' ? this.getSelectedAllergenValues(row)[0] || null : null,
      resolvedAllergenCodes: action === 'approved' ? this.getSelectedAllergenValues(row) : [],
    })))
      .then(() => {
        this.selectedReviewRowIds = [];
        if ((this as any).$snotify) {
          (this as any).$snotify.success(action === 'approved' ? 'Selected items approved.' : 'Selected items rejected.');
        }
        this.fetchAdminDashboard();
        if (this.showReviewQueueModal) {
          const nextPage = Math.min(this.reviewPage, this.totalReviewPages);
          this.fetchReviewQueue(nextPage);
        }
      })
      .catch(() => {
        this.loadingReviewQueue = false;
        if ((this as any).$snotify) {
          (this as any).$snotify.error('Unable to update selected items.');
        }
      });
  }

  getOverrideOptions(row: AllergenAdminReviewQueueItem) {
    const options = new Set(this.allergenOptions);
    (row.suggestedAllergens || []).forEach((option) => options.add(option));
    (row.selectedAllergens || []).forEach((option) => options.add(option));
    return Array.from(options);
  }

  getSelectedAllergenValues(row: AllergenAdminReviewQueueItem) {
    if (Object.prototype.hasOwnProperty.call(this.overrideSelections, row.id)) {
      return this.overrideSelections[row.id];
    }

    return this.getDefaultSuggestedAllergens(row);
  }

  getDefaultSuggestedAllergens(row: AllergenAdminReviewQueueItem) {
    return [...new Set((row.selectedAllergens?.length ? row.selectedAllergens : row.suggestedAllergens) || [])];
  }

  canApproveReview(row: AllergenAdminReviewQueueItem) {
    return this.getSelectedAllergenValues(row).length > 0;
  }

  isAllergenSelected(row: AllergenAdminReviewQueueItem, allergen: string) {
    return this.getSelectedAllergenValues(row).includes(allergen);
  }

  getSelectedAllergenSummary(row: AllergenAdminReviewQueueItem) {
    const values = this.getSelectedAllergenValues(row);
    if (!values.length) {
      return 'Select allergens';
    }

    if (values.length <= 2) {
      return values.join(', ');
    }

    return `${values.slice(0, 2).join(', ')} +${values.length - 2}`;
  }

  toggleAllergenDropdown(id: number) {
    this.openAllergenDropdownRowId = this.openAllergenDropdownRowId === id ? null : id;
  }

  toggleAllergenSelection(id: number, allergen: string) {
    const row = this.reviewRows.find((item) => item.id === id);
    const existing = [...(this.overrideSelections[id] || (row ? this.getDefaultSuggestedAllergens(row) : []))];
    const nextValues = existing.includes(allergen)
      ? existing.filter((item) => item !== allergen)
      : Array.from(new Set([...existing, allergen]));
    this.$set(this.overrideSelections, id, nextValues);
  }

  handleDocumentClick() {
    this.openAllergenDropdownRowId = null;
  }

  isReviewRowSelected(id: number) {
    return this.selectedReviewRowIds.includes(id);
  }

  toggleReviewRowSelection(id: number, event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.selectedReviewRowIds = target?.checked
      ? Array.from(new Set([...this.selectedReviewRowIds, id]))
      : this.selectedReviewRowIds.filter((selectedId) => selectedId !== id);
  }

  toggleSelectAllVisibleReviews(event: Event) {
    const target = event.target as HTMLInputElement | null;
    const visibleIds = this.paginatedReviewRows.map((row) => row.id);
    this.selectedReviewRowIds = target?.checked
      ? Array.from(new Set([...this.selectedReviewRowIds, ...visibleIds]))
      : this.selectedReviewRowIds.filter((id) => !visibleIds.includes(id));
  }

  canResolveSelectedReviews(action: 'approved' | 'dismissed') {
    if (!this.hasSelectedReviewRows || this.loadingReviewQueue) {
      return false;
    }

    if (action === 'dismissed') {
      return true;
    }

    return this.reviewRows
      .filter((row) => this.selectedReviewRowIds.includes(row.id))
      .every((row) => this.getSelectedAllergenValues(row).length > 0);
  }

  handleReviewSearch(event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.reviewSearch = target?.value || '';
    this.fetchReviewQueue(1);
  }

  updateReviewPageSize(event: Event) {
    const target = event.target as HTMLSelectElement | null;
    const nextSize = Number(target?.value || this.reviewPageSize);
    this.reviewPageSize = [20, 50, 100].includes(nextSize) ? nextSize : 20;
    this.fetchReviewQueue(1);
  }

  goToReviewPage(page: number) {
    const nextPage = Math.min(Math.max(page, 1), this.totalReviewPages);
    if (nextPage !== this.reviewPage) {
      this.fetchReviewQueue(nextPage);
    }
  }

  getReviewRowSerial(row: AllergenAdminReviewQueueItem) {
    const index = this.paginatedReviewRows.findIndex((item) => item.id === row.id);
    return index >= 0 ? ((this.reviewPage - 1) * this.reviewPageSize) + index + 1 : '';
  }

  formatCompactCount(value: number) {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }

    if (value >= 1000) {
      return `${Math.round(value / 1000)}K`;
    }

    return value.toLocaleString();
  }

  formatProcessed(processedCount: number, totalCount: number) {
    return `${processedCount.toLocaleString()} / ${totalCount.toLocaleString()}`;
  }

  formatNullableCount(value: number | null | undefined, fallback = 'Full catalog') {
    return typeof value === 'number' ? value.toLocaleString() : fallback;
  }

  formatBatchProgress(currentBatchNumber: number, totalBatchCount: number) {
    if (!totalBatchCount) {
      return '0 / 0';
    }

    return `${Math.min(Math.max(currentBatchNumber, 0), totalBatchCount).toLocaleString()} / ${totalBatchCount.toLocaleString()}`;
  }

  formatConfidence(row: AllergenAdminReviewQueueItem) {
    if (typeof row.confidence === 'number') {
      return `${Math.round(row.confidence * 100)}% (${row.confidenceBand})`;
    }

    return row.confidenceBand || 'Unknown';
  }

  formatUtcTimestamp(value: string | null | undefined) {
    if (!value) {
      return 'Unknown';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  }

  formatPercent(value: number | null | undefined) {
    const safeValue = typeof value === 'number' && Number.isFinite(value) ? value : 0;
    return `${(safeValue * 100).toFixed(1)}%`;
  }

  startDashboardPolling() {
    this.stopDashboardPolling();
    this.dashboardPollingHandle = window.setInterval(() => {
      this.fetchAdminDashboard();
    }, 20000);
  }

  stopDashboardPolling() {
    if (this.dashboardPollingHandle !== null) {
      window.clearInterval(this.dashboardPollingHandle);
      this.dashboardPollingHandle = null;
    }
  }
}

export default AdminAllergensAi;
